import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Link,
  Tooltip,
  IconButton,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import StorageIcon from '@mui/icons-material/Storage';

interface InternalToolsCardProps {
  requestKey: string;
}

export function InternalToolsCard({ requestKey }: InternalToolsCardProps) {
  const [copied, setCopied] = useState(false);

  if (!requestKey) return null;

  const shortenedKey = requestKey.length > 20
    ? `${requestKey.slice(0, 10)}...${requestKey.slice(-8)}`
    : requestKey;

  const datastoreUrl = `https://console.cloud.google.com/datastore/entities/edit?project=fancyhands-hrd&key=${requestKey}`;

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(requestKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BuildIcon color="primary" />
          <Typography variant="h6">Internal</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Key with copy */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Key
              </Typography>
              <Tooltip title={requestKey} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                  }}
                  onClick={handleCopyKey}
                >
                  {shortenedKey}
                </Typography>
              </Tooltip>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy key'}>
              <IconButton size="small" onClick={handleCopyKey}>
                {copied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Datastore link */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <StorageIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Datastore
              </Typography>
              <Typography variant="body2">
                <Link
                  href={datastoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in Console
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
